import { EditorState, Modifier } from 'draft-js';

import { IconAddPlus } from '@components/icons/add-plus';
import { IconTemplate } from '@components/icons/template';
import { Menu } from '@components/menu';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { htmlToDraft } from '@utils/draftjs';

import { useTextEditorContext } from './text-editor';

const insertMessage = (editorState: EditorState, message: string) => {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection();
  const blocksFromHTML = htmlToDraft(message);
  const newContentState = Modifier.replaceWithFragment(
    currentContent,
    selection,
    blocksFromHTML.getBlockMap()
  );
  return EditorState.push(editorState, newContentState, 'insert-fragment');
};

type TemplateType = {
  id: string;
  title: string;
  contentHtml: string;
  templateType: string;
};

export const TextEditorMessageTemplate = ({
  onSelect,
  templates = [],
  canCreate,
}: {
  onSelect: () => void;
  templates?: TemplateType[];
  canCreate: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const { readOnly, editorState, setEditorState } = useTextEditorContext();
  if (readOnly) return null;

  const messageTemplates = templates.filter(
    (email: TemplateType) => email.templateType === 'message'
  );
  return (
    <Menu
      data-c="placeholder-trigger"
      buttonProps={{
        iconRight: <IconTemplate />,
        variant: 'secondary',
        font: 'regular16',
        color: 'purple50',
        size: 'sm',
      }}
    >
      {canCreate && (
        <Stack space={4}>
          <Menu.Item icon={<IconAddPlus />} label={t('createTemplate')} onSelect={onSelect} />
          <Separator />
        </Stack>
      )}
      <Menu.Label label={t('menuLabel')} />
      {messageTemplates.length === 0 ? (
        <Menu.Description text={t('noTemplates')} />
      ) : (
        messageTemplates
          .sort((a, b) => a.title.localeCompare(b.title))
          .map((email: TemplateType) => (
            <Menu.Item
              key={email.id}
              label={email.title}
              onSelect={() => {
                const messageState = insertMessage(editorState, email.contentHtml);
                if (messageState) {
                  setEditorState(messageState);
                }
              }}
            />
          ))
      )}
    </Menu>
  );
};

const locale = {
  en: {
    menuLabel: 'Message templates',
    noTemplates: 'No message templates',
    createTemplate: 'Create template',
  },
  is: {
    menuLabel: 'Sniðmát',
    noTemplates: 'Engin sniðmát',
    createTemplate: 'Búa til sniðmát',
  },
};
